import React, { forwardRef, useImperativeHandle } from "react";
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import SimpleTextRenderer from '../aggrid-cell-renderers/simple-text-renderer'
import ActionsRenderer from '../aggrid-cell-renderers/actions-renderer'
import ApiClient from "../../../services/api-client"
import { toast } from 'react-toastify';
import IntValRenderer from "../aggrid-cell-renderers/intval-renderer";
import IntValEditor from "../aggrid-cell-editors/intval-editor";
import useStore from "../../../hooks/useStore";
import { useEditableGrid } from './useEditableGrid';

const EMPTY_ROW = { id: undefined, name: undefined, address: undefined, subjectField: undefined, email: undefined, phone: undefined, branchNumber: undefined };

const isValid = (data) =>
    data.name != undefined && data.name != "" && data.address != undefined && data.address != "";

const CompanyBranchesGrid = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const { hasError, rowStyle, getRowStyle, initGrid, addRow, checkErrors, getItems, setItems, deleteRowLocally } = useEditableGrid({
        isValid,
        emptyRow: EMPTY_ROW,
    });

    const onGridReady = (params) => {
        initGrid(params, []);
    };

    const onCellValueChanged = (params) => {
        const nodeIndex = params.node.rowIndex;
        const countDisplayedRows = params.api.getDisplayedRowCount() - 1;
        const rowNode = params.api.getRowNode(nodeIndex);

        if (nodeIndex == countDisplayedRows && params.newValue != undefined) {
            if (params.column != undefined) {
                const colid = params.column.colId;
                if (colid === "name" || colid === "address") {
                    rowNode.setDataValue("branchNumber", "0");
                }
            }
            addRow(params.api);
            params.api.redrawRows();
        }
        params.api.refreshCells({ rowNodes: [params.node], force: true });
        checkErrors(params.api);
    };

    useImperativeHandle(ref, () => ({
        getData() {
            return getItems();
        },
        hasError() {
            return hasError;
        },
        setData(data) {
            setItems(data);
        }
    }));

    const deleteRow = (params) => {
        const rowData = [];
        const id = params.data.id;
        params.api.forEachNode(node => rowData.push(node.data));
        const companyId = useStore.getState().selectedCompany.id;

        if (id != undefined) {
            ApiClient.deleteCompanyBranch(companyId, id).then(
                response => {
                    rowData.splice(params.rowIndex, 1);
                    params.api.setRowData(rowData);
                    toast.success("deleteSucceded");
                },
                error => {
                    toast.error("Error: " + error.response.data.message);
                    console.error(error.response);
                }
            );
        } else {
            deleteRowLocally(params);
        }
    };

    return (
        <div style={{ width: '100%', height: '100%' }}>
            <div className="ag-theme-alpine editableSmGrid branchesGrid" style={{ height: 200, width: '100%' }}>
                <AgGridReact
                    onGridReady={onGridReady}
                    rowStyle={rowStyle}
                    getRowStyle={getRowStyle}
                    suppressRowHoverHighlight={true}
                    stopEditingWhenCellsLoseFocus={true}
                    style={{ width: '100%', height: '300px' }}
                    onCellValueChanged={onCellValueChanged}
                    borders={true}
                    singleClickEdit={true}
                    skipHeaderOnAutoSize={true}
                    defaultColDef={{
                        flex: 1,
                        resizable: false,
                        sortable: false,
                        wrapText: true,
                        suppressMovable: true,
                    }}
                    columnDefs={[
                        { field: "logofile", hide: true },
                        { field: "name", flex: 3, minWidth: 200, headerName: t("branchName"), headerClass: "required", sortable: true, editable: true, filter: "simpleTextRenderer", cellRenderer: SimpleTextRenderer },
                        { field: "address", flex: 3, minWidth: 200, headerName: t("Address"), headerClass: "required", sortable: true, editable: true, filter: "agNumberColumnFilter", cellRenderer: SimpleTextRenderer },
                        { field: "branchNumber", flex: 1, minWidth: 100, headerName: t("BranchNumber"), headerTooltip: t("branchNameInfo"), headerClass: "required", editable: true, cellEditor: IntValEditor, cellRenderer: IntValRenderer, headerComponentParams: { template: "<span><i class='fa fa-info-circle percentInfoIcon'></i>" + t("BranchNumber") + "</span>" } },
                        { field: "subjectField", flex: 3, minWidth: 200, headerName: t("subjectField"), sortable: true, editable: true, filter: "agNumberColumnFilter", cellRenderer: SimpleTextRenderer },
                        { field: "email", flex: 2, minWidth: 200, headerName: t("Email"), sortable: true, editable: true, filter: "simpleTextRenderer", cellRenderer: SimpleTextRenderer },
                        { field: "phone", flex: 2, minWidth: 200, headerName: t("Phone"), sortable: true, editable: true, filter: "simpleTextRenderer", cellRenderer: SimpleTextRenderer },
                        { field: "action", flex: 1, minWidth: 100, headerName: t("Actions"), cellRenderer: ActionsRenderer, cellRendererParams: { deleteRow: deleteRow } }
                    ]}
                >
                </AgGridReact>
            </div>
        </div>
    );
});
export default CompanyBranchesGrid;
